import * as Sentry from "@sentry/react";
import { attachmentsCreate, attachmentsFinalize } from "@/api/generated/sdk.gen";
import { listUploads, updateUpload, deleteUpload, type UploadRow } from "@/lib/upload-queue";
import { readDraft, writeDraft } from "@/lib/drafts";
import { emitProgress, clearProgress, emitComplete } from "@/lib/upload-progress";

const DEFAULT_BACKOFF_MS = [5_000, 30_000, 120_000] as const;
const DEFAULT_POLL_MS = 30_000;
const PROGRESS_THROTTLE_MS = 100;

function putWithProgress(opts: {
  url: string;
  body: Blob;
  contentType: string;
  signal: AbortSignal;
  tempId: string;
}): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let lastEmit = 0;
    xhr.upload.onprogress = (e) => {
      if (!e.total) return;
      const now = Date.now();
      if (now - lastEmit < PROGRESS_THROTTLE_MS && e.loaded < e.total) return;
      lastEmit = now;
      emitProgress(opts.tempId, e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        emitProgress(opts.tempId, 1);
        resolve();
      } else {
        reject(new Error(`PUT failed ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error("PUT network error"));
    xhr.onabort = () => reject(new Error("PUT aborted"));
    opts.signal.addEventListener("abort", () => xhr.abort(), { once: true });
    xhr.open("PUT", opts.url);
    xhr.setRequestHeader("Content-Type", opts.contentType);
    xhr.send(opts.body);
  });
}

let aborted = false;
let running = false;

export interface DispatcherOptions {
  backoffMs?: readonly number[];
  pollMs?: number;
}

/**
 * Recursively walk a JSON-serialisable draft and:
 *   1. Replace any AttachmentValue-shaped `{kind: "temp", tempId}` with
 *      `{kind: "attachment", id: realId}`.
 *   2. Replace any bare string that equals `tempId` with `realId` (covers
 *      callers that store the temp id outside the AttachmentValue shape).
 * Returns a new value; does not mutate the input.
 */
export function swapTempIdInDraft(value: unknown, tempId: string, realId: string): unknown {
  if (typeof value === "string") {
    return value === tempId ? realId : value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => swapTempIdInDraft(item, tempId, realId));
  }
  if (value !== null && typeof value === "object") {
    const obj = value as Record<string, unknown>;
    if (obj.kind === "temp" && obj.tempId === tempId) {
      return { kind: "attachment", id: realId };
    }
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj)) {
      out[k] = swapTempIdInDraft(v, tempId, realId);
    }
    return out;
  }
  return value;
}

async function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function processRow(row: UploadRow): Promise<void> {
  await updateUpload(row.tempId, { status: "uploading" });

  const presign = await attachmentsCreate({
    body: { filename: row.filename, mimeType: row.contentType, byteSize: row.file.size },
  });

  if (!presign.data) {
    throw new Error(`attachmentsCreate failed: ${JSON.stringify(presign.error)}`);
  }

  const { id, presignedUrl } = presign.data;

  const controller = new AbortController();
  await putWithProgress({
    url: presignedUrl,
    body: row.file,
    contentType: row.contentType,
    signal: controller.signal,
    tempId: row.tempId,
  });

  const finalize = await attachmentsFinalize({ path: { id } });

  if (!finalize.data) {
    throw new Error(`attachmentsFinalize failed: ${JSON.stringify(finalize.error)}`);
  }

  // Swap tempId → real id in the parent draft so a future page reload
  // picks up the finalized attachment.
  const draft = await readDraft<unknown>(row.parentDraftKey);
  if (draft) {
    const updated = swapTempIdInDraft(draft.data, row.tempId, id);
    await writeDraft(row.parentDraftKey, updated);
  }

  // Notify any mounted PhotoUploader so its in-memory form state flips
  // from {kind:"temp"} to {kind:"attachment"}.
  emitComplete(row.tempId, id);

  await deleteUpload(row.tempId);
  clearProgress(row.tempId);
}

async function dispatchLoop(opts: Required<DispatcherOptions>): Promise<void> {
  while (!aborted) {
    const rows = await listUploads();
    const pending = rows.find((r) => r.status === "pending");

    if (!pending) {
      await sleep(opts.pollMs);
      continue;
    }

    try {
      await processRow(pending);
    } catch (err) {
      Sentry.addBreadcrumb({
        category: "upload-dispatcher",
        message: `Upload failed for tempId=${pending.tempId}: ${String(err)}`,
        level: "error",
      });

      const newRetries = pending.retries + 1;
      const maxRetries = opts.backoffMs.length + 1;

      if (newRetries >= maxRetries) {
        await updateUpload(pending.tempId, {
          status: "failed",
          retries: newRetries,
          lastError: String(err),
        });
        clearProgress(pending.tempId);
      } else {
        await updateUpload(pending.tempId, {
          status: "pending",
          retries: newRetries,
          lastError: String(err),
        });
        const delay = opts.backoffMs[newRetries - 1];
        if (delay !== undefined) {
          await sleep(delay);
        }
      }
    }
  }
}

export function startDispatcher(opts: DispatcherOptions = {}): void {
  if (running) return;
  aborted = false;
  running = true;

  const resolved: Required<DispatcherOptions> = {
    backoffMs: opts.backoffMs ?? DEFAULT_BACKOFF_MS,
    pollMs: opts.pollMs ?? DEFAULT_POLL_MS,
  };

  void dispatchLoop(resolved).finally(() => {
    running = false;
  });
}

export function stopDispatcher(): void {
  aborted = true;
  running = false;
}

/** Resets dispatcher state. For tests only. */
export function __resetDispatcherForTests(): void {
  aborted = false;
  running = false;
}
